// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { shippingWeightUnit } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";

type Props = WidgetProps & HasProduct;

export interface ProductDetailsInfoStyles {
    infoSection?: InjectableCss;
    infoWrapper?: InjectableCss;
    labelValueStyles?: SmallHeadingAndTextStyles;
}

export const productDetailsInfoStyles: ProductDetailsInfoStyles = {};

const styles = productDetailsInfoStyles;

const ProductDetailsInfo: React.FC<Props> = ({ product }) => {
    if (!product) {
        return null;
    }
    let showProductSize = false;
    if (
        ((product as any).detail.shippingHeight && (product as any).detail.shippingHeight !== 0) ||
        ((product as any).detail.shippingWidth && (product as any).detail.shippingWidth !== 0) ||
        ((product as any).detail.shippingLength && (product as any).detail.shippingLength !== 0)
    ) {
        showProductSize = true;
    }
    const shippingHeight =
        ((product as any).detail.shippingHeight && Number((product as any).detail.shippingHeight)) || 0;
    const shippingWidth = ((product as any).detail.shippingWidth && Number((product as any).detail.shippingWidth)) || 0;
    const shippingLength =
        ((product as any).detail.shippingLength && Number((product as any).detail.shippingLength)) || 0;

    const dimensions = [shippingLength, shippingWidth, shippingHeight];

    const itemSize = dimensions
        .filter(dim => !!dim)
        .map(dim => `${dim}"`)
        .join(" x ");

    return (
        <StyledWrapper {...styles.infoWrapper}>
            <StyledWrapper {...styles.infoSection}>
                {createWidgetElement("ProductDetails/ProductDetailsPartNumber", {
                    fields: {},
                })}
            </StyledWrapper>
            {!!(product as any).detail.shippingWeight && (
                <StyledWrapper {...styles.infoSection}>
                    <SmallHeadingAndText
                        heading={translate("Shipping Weight")}
                        text={`${(product as any).detail.shippingWeight} ${shippingWeightUnit}`}
                        extendedStyles={styles.labelValueStyles}
                    ></SmallHeadingAndText>
                </StyledWrapper>
            )}

            {showProductSize && (shippingHeight !== 0 || shippingWidth !== 0 || shippingLength !== 0) && (
                <StyledWrapper {...styles.infoSection}>
                    <SmallHeadingAndText
                        heading={translate("Item Size")}
                        text={itemSize}
                        extendedStyles={styles.labelValueStyles}
                    ></SmallHeadingAndText>
                </StyledWrapper>
            )}
            {product.packDescription && (
                <StyledWrapper {...styles.infoSection}>
                    <SmallHeadingAndText
                        heading={translate("Case Pack")}
                        text={product.packDescription}
                        extendedStyles={styles.labelValueStyles}
                    ></SmallHeadingAndText>
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.infoSection}>
                {createWidgetElement("ProductDetails/ProductDetailsManufacturingPartNumber", {
                    fields: {},
                })}
            </StyledWrapper>
            <StyledWrapper {...styles.infoSection}>
                {createWidgetElement("ProductDetails/ProductDetailsCustomerPartNumber", {
                    fields: {},
                })}
            </StyledWrapper>

            {/* Avoid showing Empty values */}
            {!!product.properties.countryOfOrigin?.trim() && (
                <StyledWrapper {...styles.infoSection}>
                    <SmallHeadingAndText
                        heading={translate("Country Of Origin")}
                        text={product.properties.countryOfOrigin.trim()}
                        extendedStyles={styles.labelValueStyles}
                    ></SmallHeadingAndText>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsInfo),
    definition: {
        displayName: "Product Details Info",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
